import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { User } from "../../types";
import { useApi } from "../ApiProvider";
import "./MembershipProvider.css";
// 사용자 역할 타입 정의
export type UserRole = "guest" | "user" | "premium" | "enterprise" | "admin";
export type EmbedRole = "pc" | "mobile";

// 멤버십 상태 인터페이스
export interface MembershipState {
  user: User | null;
  isAuthenticated: boolean;
  role: UserRole;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  embed: EmbedRole;
}

// 멤버십 컨텍스트 인터페이스
export interface MembershipContextType extends MembershipState {
  login: (
    user: User,
    accessToken: string,
    refreshToken?: string,
    embed?: boolean
  ) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  updateRole: (role: UserRole) => void;
  clearError: () => void;
  refreshAccessToken: () => Promise<boolean>;
  refreshUser: () => Promise<boolean>;
  // 권한 확인 함수들
  hasAdminPermission: () => boolean;
  hasManagerPermission: () => boolean;
  hasSystemPermission: () => boolean;
  hasUserPermission: () => boolean;
  isEmbed: () => boolean;
}

// 기본 상태
const defaultState: MembershipState = {
  user: null,
  isAuthenticated: false,
  role: "guest",
  accessToken: null,
  refreshToken: null,
  isLoading: false,
  isInitialized: false,
  error: null,
  embed: "pc",
};

// 컨텍스트 생성
const MembershipContext = createContext<MembershipContextType | undefined>(
  undefined
);

// 로컬 스토리지 키
const STORAGE_KEYS = {
  KUID: "kuid",
  USER: "membership_user",
  ACCESS_TOKEN: "membership_access_token",
  REFRESH_TOKEN: "membership_refresh_token",
  ROLE: "membership_role",
  EMBED: "embed",
};

// 멤버십 프로바이더 컴포넌트
interface MembershipProviderProps {
  children: ReactNode;
}

export const MembershipProvider: React.FC<MembershipProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<MembershipState>(defaultState);
  const { getProfile, getMyHome } = useApi();

  // 초기화 시 로컬 스토리지에서 사용자 정보 복원
  useEffect(() => {
    const initializeMembership = async () => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }));

        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const storedAccessToken = localStorage.getItem(
          STORAGE_KEYS.ACCESS_TOKEN
        );
        const storedRefreshToken = localStorage.getItem(
          STORAGE_KEYS.REFRESH_TOKEN
        );
        const storedRole = localStorage.getItem(STORAGE_KEYS.ROLE) as UserRole;
        const storedEmbed = localStorage.getItem(
          STORAGE_KEYS.EMBED
        ) as EmbedRole;

        if (storedUser && storedAccessToken) {
          const user = JSON.parse(storedUser);
          setState({
            user,
            isAuthenticated: true,
            role: storedRole || "user",
            accessToken: storedAccessToken,
            refreshToken: storedRefreshToken,
            isLoading: false,
            isInitialized: true,
            error: null,
            embed: storedEmbed || "pc",
          });
        } else {
          // 인증 정보가 없는 경우 초기화 완료
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isInitialized: true,
          }));
        }
      } catch (error) {
        console.error("멤버십 상태 복원 실패:", error);
        // 로컬 스토리지 데이터가 손상된 경우 초기화
        clearLocalStorage();
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isInitialized: true,
        }));
      }
    };

    initializeMembership();
  }, []);

  // 로컬 스토리지 정리 함수
  const clearLocalStorage = () => {
    // Object.values(STORAGE_KEYS).forEach((key) => {
    //   localStorage.removeItem(key);
    // });
    localStorage.clear();
  };

  // 로그인 함수
  const login = (
    user: User,
    accessToken: string,
    refreshToken?: string,
    embed?: boolean
  ) => {
    try {
      // 🎯 중복 로그인 방지: 이미 같은 사용자로 로그인되어 있으면 무시
      if (state.isAuthenticated && state.user?.id === user.id) {
        console.log("이미 로그인된 사용자입니다. 중복 로그인 방지:", user.id);
        return;
      }

      let role: UserRole = "user";

      if (user.role) {
        role = user.role as UserRole;
      }

      const finalAccessToken =
        accessToken || localStorage.getItem("authToken") || "";

      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, finalAccessToken);
      if (refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
      localStorage.setItem(STORAGE_KEYS.ROLE, role);
      localStorage.setItem(
        STORAGE_KEYS.EMBED,
        embed === true ? "mobile" : "pc"
      );

      // 상태 업데이트
      setState({
        user,
        isAuthenticated: true,
        role,
        accessToken: finalAccessToken,
        refreshToken: refreshToken || null,
        isLoading: false,
        isInitialized: true,
        error: null,
        embed: embed === true ? "mobile" : "pc",
      });

      // 🎯 디버그 로그 제거하여 무한 루프 방지
      // console.log("로그인 성공:", {
      //   user,
      //   role,
      //   accessToken: finalAccessToken,
      // });
    } catch (error) {
      console.error("로그인 처리 실패:", error);
      setState((prev) => ({
        ...prev,
        error: "로그인 처리 중 오류가 발생했습니다.",
        isLoading: false,
      }));
    }
  };

  // 로그아웃 함수
  const logout = async () => {
    try {
      // 로컬 스토리지 정리
      clearLocalStorage();

      // 상태 초기화
      setState({
        user: null,
        isAuthenticated: false,
        role: "guest",
        accessToken: null,
        refreshToken: null,
        isLoading: false,
        isInitialized: true,
        error: null,
        embed: (localStorage.getItem(STORAGE_KEYS.EMBED) as EmbedRole) || "pc",
      });

      console.log("로그아웃 완료");
    } catch (error) {
      console.error("로그아웃 처리 실패:", error);
    }
  };

  // 사용자 정보 업데이트 함수
  const updateUser = (userData: Partial<User>) => {
    if (!state.user) return;

    try {
      const updatedUser = { ...state.user, ...userData };

      // 로컬 스토리지 업데이트
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      // 상태 업데이트
      setState((prev) => ({
        ...prev,
        user: updatedUser,
      }));

      console.log("사용자 정보 업데이트:", userData);
    } catch (error) {
      console.error("사용자 정보 업데이트 실패:", error);
      setState((prev) => ({
        ...prev,
        error: "사용자 정보 업데이트 중 오류가 발생했습니다.",
      }));
    }
  };

  // 역할 업데이트 함수
  const updateRole = (role: UserRole) => {
    try {
      // 로컬 스토리지 업데이트
      localStorage.setItem(STORAGE_KEYS.ROLE, role);

      // 상태 업데이트
      setState((prev) => ({
        ...prev,
        role,
      }));

      console.log("역할 업데이트:", role);
    } catch (error) {
      console.error("역할 업데이트 실패:", error);
      setState((prev) => ({
        ...prev,
        error: "역할 업데이트 중 오류가 발생했습니다.",
      }));
    }
  };

  // 에러 정리 함수
  const clearError = () => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  };

  // 액세스 토큰 갱신 함수
  const refreshAccessToken = async (): Promise<boolean> => {
    if (!state.refreshToken) {
      console.error("리프레시 토큰이 없습니다.");
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: state.refreshToken }),
      });

      if (!response.ok) {
        throw new Error("토큰 갱신 실패");
      }

      const { access_token, refresh_token: newRefreshToken } =
        await response.json();

      // 로컬 스토리지 업데이트
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
      if (newRefreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
      }

      // 상태 업데이트
      setState((prev) => ({
        ...prev,
        accessToken: access_token,
        refreshToken: newRefreshToken || prev.refreshToken,
        isLoading: false,
        error: null,
      }));

      console.log("액세스 토큰 갱신 성공");
      return true;
    } catch (error) {
      console.error("액세스 토큰 갱신 실패:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "토큰 갱신에 실패했습니다. 다시 로그인해주세요.",
      }));

      // 토큰 갱신 실패 시 로그아웃
      logout();
      return false;
    }
  };

  // 사용자 정보 새로고침 함수
  const refreshUser = useCallback(async (): Promise<any> => {
    if (!state.isAuthenticated) {
      console.error("인증되지 않은 사용자입니다.");
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      // ApiProvider의 profile API 호출
      const userData = await getMyHome(); //getProfile();

      // User 타입으로 변환 (기존 사용자 정보를 유지하면서 API 데이터로 업데이트)
      const updatedUser: User = {
        ...state.user,
        ...userData?.user_profile,
        phone_number: userData?.user_profile?.phone_number ?? undefined,
      };

      // 로컬 스토리지 업데이트
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));

      // 상태 업데이트
      setState((prev) => ({
        ...prev,
        user: updatedUser,
        isLoading: false,
        error: null,
      }));

      // console.log('사용자 정보 새로고침 성공:', updatedUser);
      return userData;
    } catch (error) {
      console.error("사용자 정보 새로고침 실패:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "사용자 정보 새로고침에 실패했습니다.",
      }));
      return null;
    }
  }, [state.isAuthenticated, state.user, getProfile]);

  // 임베딩 모드 확인 함수
  const isEmbed = (): boolean => {
    return state.embed === "mobile";
  };

  // 권한 확인 함수들
  const hasAdminPermission = (): boolean => {
    return state.user?.is_admin === true;
  };

  const hasManagerPermission = (): boolean => {
    return state.user?.is_manager === true;
  };

  const hasSystemPermission = (): boolean => {
    return state.user?.is_system === true;
  };

  const hasUserPermission = (): boolean => {
    return state.user?.is_user === true;
  };

  // 컨텍스트 값
  const contextValue: MembershipContextType = {
    ...state,
    login,
    logout,
    updateUser,
    updateRole,
    clearError,
    refreshAccessToken,
    refreshUser,
    isEmbed,
    hasAdminPermission,
    hasManagerPermission,
    hasSystemPermission,
    hasUserPermission,
  };

  return (
    <MembershipContext.Provider value={contextValue}>
      {children}
    </MembershipContext.Provider>
  );
};

// 커스텀 훅
export const useMembership = (): MembershipContextType => {
  const context = useContext(MembershipContext);
  if (context === undefined) {
    throw new Error(
      "useMembership은 MembershipProvider 내부에서 사용되어야 합니다."
    );
  }
  return context;
};

export default MembershipProvider;
